import {FEATURE_CONSTANTS, ACCOUNT_TYPE_ID} from '@advisor/util/constants';

export default {
  INDIVIDUAL: {
    accountTypeId: ACCOUNT_TYPE_ID.INDIVIDUAL,
    accountType: 'INDIVIDUAL',
    title: 'Individual',
    description:
      'This account is useful for all types of investment reasons for a single owner. It has no contribution limits, but it’s subject to annual capital gains.',
    featureStatus: false,
    featureConstant: FEATURE_CONSTANTS.INDIVIDUAL,
    isVisible: true,
    isDisabled: false,
  },

  TRADITIONAL_IRA: {
    accountTypeId: ACCOUNT_TYPE_ID.TRADITIONAL_IRA,
    accountType: 'TRADITIONAL_IRA',
    title: 'Traditional IRA',
    description:
      'This type of retirement account allows you to invest your money for the long term and pay taxes later. You may be able to deduct your IRA contributions from your taxes in the year you made the contributions, which lowers your taxable income for that year. You then pay taxes on the money when you withdraw it in retirement (based on your income at the time of withdrawal).',
    featureStatus: false,
    featureConstant: FEATURE_CONSTANTS.TRADITIONAL_IRA,
    isVisible: true,
    isDisabled: false,
  },

  ROTH_IRA: {
    accountTypeId: ACCOUNT_TYPE_ID.ROTH_IRA,
    accountType: 'ROTH_IRA',
    title: 'Roth IRA',
    description:
      'This type of retirement account allows you to invest your money for the long term and pay taxes now. You contribute using after-tax money (taxes are based on your income at the time of contribution), without deducting those IRA contributions from your taxes. You may then qualify to withdraw your money, tax-free when you reach retirement.',
    featureStatus: false,
    featureConstant: FEATURE_CONSTANTS.ROTH_IRA,
    isVisible: true,
    isDisabled: false,
  },

  SEP_IRA: {
    accountTypeId: ACCOUNT_TYPE_ID.SEP_IRA,
    accountType: 'SEP_IRA',
    title: 'SEP IRA',
    description:
      'Small business owner? This is the retirement account for you, as it’s tax-deferred and contributions are made by the employer.',
    featureStatus: false,
    featureConstant: FEATURE_CONSTANTS.SEP_IRA,
    isVisible: true,
    isDisabled: false,
  },

  SIMPLE_IRA: {
    accountTypeId: ACCOUNT_TYPE_ID.SIMPLE_IRA,
    accountType: 'SIMPLE_IRA',
    title: 'SIMPLE IRA',
    description:
      'While less common, this tax-deferred account is typically offered by small business owners. It lets employees contribute to their retirement savings as part of their pre-tax compensation, and employers can contribute too.',
    featureStatus: false,
    featureConstant: FEATURE_CONSTANTS.SIMPLE_IRA,
    isVisible: true,
    isDisabled: false,
  },

  GOAL: {
    title: 'Goal',
    description:
      'Within the client portal, configure the access your clients will have for adding a new goal and attaching accounts to existing goals. Client updates to these features could result in trade activity and updated asset allocations.  If no goals are set up in the Onboarding - Goals section, the add goal feature cannot be turned on.',
    permissionList: [
      {
        name: 'Add Goal',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.ADD_EDIT_DELETE_GOAL,
        isVisible: true,
        isDisabled: false,
      },
      {
        name: 'Assign Accounts',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.ATTACH_ACCOUNT,
        isVisible: true,
        isDisabled: false,
      },
      {
        name: 'Target Date',
        featureStatus: false,
        featureConstant: null,
        isVisible: true,
        isDisabled: false,
      },
    ],
  },

  ACCOUNT: {
    title: 'Account',
    description:
      'Within client portal configure the ability for your clients to open new accounts and link or transfer in accounts from external financial institutions.',
    permissionList: [
      {
        name: 'Open Account',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.OPEN_ACCOUNT,
        isVisible: true,
        isDisabled: false,
      },

      {
        name: 'Account Aggregation',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.ACCOUNT_AGGREGATION,
        isVisible: true,
        isDisabled: false,
      },
      {
        name: 'ACATS',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.ACATS,
        isVisible: true,
        isDisabled: false,
      },
    ],
  },

  TRANSFER_MONEY: {
    title: 'Transfer Money',
    description:
      'Within the client portal, configure the access for clients to execute deposits and withdrawals for their accounts.',
    permissionList: [
      {
        name: 'Deposits & Withdrawals',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.BANK_TRANSFER,
        isVisible: true,
        isDisabled: false,
      },
      {
        name: 'Withdraw',
        featureStatus: false,
        featureConstant: null,
        isVisible: true,
        isDisabled: false,
      },
    ],
  },

  PERFORMANCE_REPORTING: {
    title: 'Performance Reporting',
    description:
      'Within the client portal, determine the level of performance reporting the client can view. This can be set at the Goal or Account Level.',
    permissionList: [
      {
        name: 'Goal Level',
        featureStatus: false,
        featureConstant: null,
        isVisible: true,
        isDisabled: false,
      },
      {
        name: 'Performance Reports',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.PERFORMANCE_REPORTING,
        isVisible: true,
        isDisabled: false,
      },
    ],
  },

  ASSET_ALLOCATION: {
    title: 'Asset Allocation',
    description:
      'Within the client portal, determine where the client can review the asset allocation. There is also a configuration to control the display of the current and target portfolios.',
    permissionList: [
      {
        name: 'Goal Level (Current Portfolio)',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.CURRENT_PORTFOLIO_GOAL,
        isVisible: true,
        isDisabled: false,
      },
      {
        name: 'Goal Level (Target Portfolio)',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.TARGET_PORTFOLIO_GOAL,
        isVisible: true,
        isDisabled: false,
      },
      {
        name: 'Account Level (Current Portfolio)',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.CURRENT_PORTFOLIO_ACCOUNT,
        isVisible: true,
        isDisabled: false,
      },
      {
        name: 'Account Level (Target Portfolio)',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.TARGET_PORTFOLIO_ACCOUNT,
        isVisible: true,
        isDisabled: false,
      },
    ],
  },

  WEALTH_PROJECTION: {
    title: 'Wealth Projection',
    description: 'Within the client portal, determine where the client can review the wealth projection.',
    permissionList: [
      {
        name: 'Goal Level',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.WEALTH_PROJECTION_GOAL,
        isVisible: true,
        isDisabled: false,
      },
      {
        name: 'Account Level',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.WEALTH_PROJECTION_ACCOUNT,
        isVisible: true,
        isDisabled: false,
      },
    ],
  },

  RISK_TOLERANCE: {
    title: 'Risk Tolerance',
    description:
      'Within the client portal, configure the access your clients will have for overriding their risk score and retaking the risk tolerance questionnaire. These features could result in trade activity or updates to their asset allocation if updated.',
    permissionList: [
      {
        name: 'Score Override',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.RISK_TOL_OVERRIDE,
        isVisible: true,
        isDisabled: false,
      },
      {
        name: 'Questionnaire',
        featureStatus: false,
        featureConstant: FEATURE_CONSTANTS.RISK_TOL_QUEST,
        isVisible: true,
        isDisabled: false,
      },
    ],
  },
};
